import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BUSINESS_METRICS, EVIDENCE_STRENGTH_LABEL, LEVERS, MEASUREMENT_PERIOD_NOTE, RETENTION_NOTE } from '../data/model';
import type { EvidenceStrength } from '../data/model';

const strengthBadgeVariant = (strength: EvidenceStrength) =>
  strength === 'strong' ? 'success' : strength === 'good_indirect' ? 'warning' : 'muted';

/** Explains the model's stance, the evidence-vs-assumption split per metric, and measurement guidance. */
export const MethodologySection: React.FC = () => (
  <div className='flex flex-col gap-4'>
    <div>
      <h2 className='text-base font-semibold'>Methodology & Sources</h2>
      <p className='text-sm text-muted-foreground'>
        How the ROI is derived and, for every figure, what comes directly from published evidence versus what we assumed
        ourselves.
      </p>
    </div>

    {/* Prose caps out around 80ch for readability, so the two narrative cards
        sit in a two-column row instead of one full-width column each. */}
    <div className='grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]'>
      <Card>
        <CardHeader>
          <CardTitle>The stance</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-3 text-sm text-muted-foreground'>
          <p>
            Each biometric measure (sleep duration, sleep quality, resting heart rate, HRV, vigorous activity) is
            captured as a cohort average at "then" (the start of the programme) and "now" (currently), and converted to
            a £ value using{' '}
            <span className='font-medium text-foreground'>one piece of published evidence per measure</span>. Where the
            study doesn't map cleanly onto a continuous WHOOP score or a per-unit rate, we state a{' '}
            <span className='font-medium text-foreground'>bridging assumption</span> (the specific step we added
            ourselves), separately from the study finding, so it's clear which part is evidence and which part is our
            judgement.
          </p>
          <p>
            Participation rate and attribution rate then scale every biometric lever: participation is a measured
            Management Hub figure, attribution is the company's own judgement call on how much of the change to credit
            to the programme. The business metrics below (staff absence, injuries, overtime, turnover, employee
            wellbeing) are different: they're confirmed company-reported totals, read directly off your own records
            as a "then vs now" cost, so neither participation/attribution scaling nor a research citation applies —
            they're not estimates.
          </p>
          <p>
            If there isn't published research to support a financial value (as with staff retention), we show £0 rather
            than invent a number. {RETENTION_NOTE}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Measurement period</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>{MEASUREMENT_PERIOD_NOTE}</p>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Evidence vs. assumption, per metric</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead>Evidence</TableHead>
              <TableHead>Study finding (published)</TableHead>
              <TableHead>Our bridging assumption</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LEVERS.map((lever) => (
              <TableRow key={lever.id}>
                <TableCell className='font-medium text-foreground'>{lever.title}</TableCell>
                <TableCell>
                  <Badge variant={strengthBadgeVariant(lever.evidenceStrength)}>
                    {EVIDENCE_STRENGTH_LABEL[lever.evidenceStrength]}
                  </Badge>
                </TableCell>
                <TableCell className='text-xs text-muted-foreground'>{lever.evidence.studyFinding}</TableCell>
                <TableCell className='text-xs text-muted-foreground'>{lever.evidence.bridgingAssumption}</TableCell>
              </TableRow>
            ))}
            {BUSINESS_METRICS.map((metric) => (
              <TableRow key={metric.id}>
                <TableCell className='font-medium text-foreground'>{metric.title}</TableCell>
                <TableCell>
                  <Badge variant='success'>Confirmed, not estimated</Badge>
                </TableCell>
                <TableCell colSpan={2} className='text-xs text-muted-foreground'>
                  Reported directly from your HR/payroll records as a total cost, then vs now — no study or
                  bridging assumption involved.
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className='font-medium text-foreground'>Staff retention</TableCell>
              <TableCell>
                <Badge variant='muted'>No evidence yet</Badge>
              </TableCell>
              <TableCell colSpan={2} className='text-xs text-muted-foreground'>
                {RETENTION_NOTE}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </div>
);
